import type { NavigateFunction } from "react-router";
import { deprecated } from "typesafe-actions";

import type { CartPositionCollection } from "@/shop/reducers/shopping/cart";
import type { IStatusError } from "@/shop/types/errors";
import type {
  IOrderConfirmationInfo,
  IOrderPlacementRequest,
  IOrderPlacementResponse,
  IOrderUpdatePartial,
  IPartialDeliveryList,
  IPriceRequestTransaction
} from "@/shop/types/shopping/order";

import { OrderActionTypes } from "./constants";

const { createStandardAction } = deprecated;

// price request

export const startPriceRequests = createStandardAction(OrderActionTypes.START_PRICE_REQUESTS).map(
  ({ collection, navigate }: { collection: CartPositionCollection; navigate: NavigateFunction }) => ({
    payload: { collection, navigate }
  })
);

export const startPriceRequestsStart = createStandardAction(OrderActionTypes.START_PRICE_REQUESTS_START)();

export const startPriceRequestsSuccess = createStandardAction(OrderActionTypes.START_PRICE_REQUESTS_SUCCESS)();

export const startPriceRequestsFail = createStandardAction(OrderActionTypes.START_PRICE_REQUESTS_FAIL).map((err: Error) => ({
  payload: err,
  error: true
}));

export const startOfferToOrder = createStandardAction(OrderActionTypes.START_OFFER_TO_ORDER).map(
  (payload: { offerId: string; navigate: NavigateFunction }) => ({ payload })
);

export const startPartialOfferToOrder = createStandardAction(OrderActionTypes.START_PARTIAL_OFFER_TO_ORDER).map(
  (payload: { offerId: string; itemNumbers: string[]; navigate: NavigateFunction }) => ({ payload })
);

// commissions list

export const fetchCommissions = createStandardAction(OrderActionTypes.FETCH_COMMISSIONS)();

export const fetchCommissionsStart = createStandardAction(OrderActionTypes.FETCH_COMMISSIONS_START)();

export const fetchCommissionsSuccess = createStandardAction(OrderActionTypes.FETCH_COMMISSIONS_SUCCESS).map((payload: IPartialDeliveryList) => ({
  payload
}));

export const fetchCommissionsFail = createStandardAction(OrderActionTypes.FETCH_COMMISSIONS_FAIL).map((err: IStatusError) => ({
  payload: err,
  error: true
}));

export const removeCommission = createStandardAction(OrderActionTypes.REMOVE_COMMISSION).map((payload: string) => ({ payload }));

// fetching price request

export const fetchPriceRequest = createStandardAction(OrderActionTypes.FETCH_PRICE_REQUEST).map((payload: string) => ({
  payload
}));

export const fetchPartialPriceRequest = createStandardAction(OrderActionTypes.FETCH_PARTIAL_PRICE_REQUEST).map((payload: string) => ({
  payload
}));

export const fetchPriceRequestStart = createStandardAction(OrderActionTypes.FETCH_PRICE_REQUEST_START).map((payload: string) => ({
  payload
}));

export const fetchPriceRequestSuccess = createStandardAction(OrderActionTypes.FETCH_PRICE_REQUEST_SUCCESS).map((payload: IPriceRequestTransaction) => ({
  payload
}));

export const fetchPriceRequestFail = createStandardAction(OrderActionTypes.FETCH_PRICE_REQUEST_FAIL).map((payload: IStatusError) => ({
  payload,
  error: true
}));

export const fetchPriceRequestProgress = createStandardAction(OrderActionTypes.FETCH_PRICE_REQUEST_PROGRESS).map((payload: number) => ({
  payload
}));

// fetch methods for further stages

export const fetchOrderDetails = createStandardAction(OrderActionTypes.FETCH_ORDER_DETAILS).map((payload: string) => ({
  payload
}));

export const fetchOrderForModify = createStandardAction(OrderActionTypes.FETCH_ORDER_FOR_MODIFY).map((payload: string) => ({
  payload
}));

export const fetchOrderForCheckout = createStandardAction(OrderActionTypes.FETCH_ORDER_FOR_CHECKOUT).map((payload: string) => ({
  payload
}));

// updating orders

/**
 * Update order while preserving existing properties
 * - this action can be awaited when using `dispatch`
 * - ⚠️ Do not `put` this in saga, this will not wait for the saga to finish
 */
export const updateOrder = createStandardAction(OrderActionTypes.UPDATE_ORDER).map((payload: IOrderUpdatePartial) => ({
  payload,
  meta: { thunk: true }
}));

export const updateOrderStart = createStandardAction(OrderActionTypes.UPDATE_ORDER_START).map(() => ({
  payload: null!
}));

export const updateOrderSuccess = createStandardAction(OrderActionTypes.UPDATE_ORDER_SUCCESS).map(
  (payload: IPriceRequestTransaction, meta: { thunk: boolean }) => ({
    payload,
    meta
  })
);

export const updateOrderFail = createStandardAction(OrderActionTypes.UPDATE_ORDER_FAIL).map((payload: IStatusError, meta: { thunk: boolean }) => ({
  payload,
  meta,
  error: true
}));

// stage switching

export const proceedToOrderEdit = createStandardAction(OrderActionTypes.PROCEED_TO_ORDER_EDIT).map((payload: { id: string; navigate: NavigateFunction }) => ({
  payload
}));

export const proceedWithCompleteDelivery = createStandardAction(OrderActionTypes.PROCEED_WITH_COMPLETE_DELIVERY).map(
  (payload: { id: string; navigate: NavigateFunction }) => ({
    payload
  })
);

export const proceedWithPartialDelivery = createStandardAction(OrderActionTypes.PROCEED_WITH_PARTIAL_DELIVERY).map(
  (payload: { id: string; navigate: NavigateFunction }) => ({
    payload
  })
);

export const proceedToOrderCheckout = createStandardAction(OrderActionTypes.PROCEED_TO_ORDER_CHECKOUT).map(
  (payload: { updatePartial: IOrderUpdatePartial; navigate: NavigateFunction }) => ({
    payload
  })
);

/**
 * Save data and go to next page
 */
export const proceedFromOrderEdit = createStandardAction(OrderActionTypes.PROCEED_FROM_ORDER_EDIT).map(
  (payload: { updatePartial: IOrderUpdatePartial; navigate: NavigateFunction }) => ({
    payload
  })
);

// order confirmation process

/**
 * Confirms order
 * - this action can be awaited when using `dispatch`
 * - ⚠️ Do not `put` this in saga, this will not wait for the saga to finish
 */
export const confirmOrder = createStandardAction(OrderActionTypes.CONFIRM_ORDER).map(
  (payload: { order: IOrderPlacementRequest; navigate: NavigateFunction; sysId: string }) => ({
    payload,
    meta: { thunk: true }
  })
);

export const confirmOrderStart = createStandardAction(OrderActionTypes.CONFIRM_ORDER_START).map(() => ({
  payload: null!
}));

export const confirmOrderSuccess = createStandardAction(OrderActionTypes.CONFIRM_ORDER_SUCCESS).map(
  (payload: IOrderPlacementResponse, meta: { thunk: boolean }) => ({
    payload,
    meta
  })
);

export const confirmOrderFail = createStandardAction(OrderActionTypes.CONFIRM_ORDER_FAIL).map((payload: IStatusError, meta: { thunk: boolean }) => ({
  payload,
  error: true,
  meta
}));

// order confirmation TODO: naming sucks!

export const fetchOrderConfirmation = createStandardAction(OrderActionTypes.FETCH_ORDER_CONFIRMATION).map((orderNo: string) => ({
  payload: orderNo
}));

export const fetchOrderConfirmationStart = createStandardAction(OrderActionTypes.FETCH_ORDER_CONFIRMATION_START).map(() => ({
  payload: null!
}));

export const fetchOrderConfirmationSuccess = createStandardAction(OrderActionTypes.FETCH_ORDER_CONFIRMATION_SUCCESS).map(
  (orderConfirmation: IOrderConfirmationInfo) => ({
    payload: orderConfirmation
  })
);

export const fetchOrderConfirmationFail = createStandardAction(OrderActionTypes.FETCH_ORDER_CONFIRMATION_FAIL).map((payload: IStatusError) => ({
  payload,
  error: true
}));

export const updatePriceRequestPosition = createStandardAction(OrderActionTypes.UPDATE_PRICE_REQUEST_POSITION).map(
  (payload: { id: string; update: { qty?: number; requestedDeliveryDate?: string } }) => ({ payload })
);

export const setWaitingForUpdate = createStandardAction(OrderActionTypes.SET_WAITING_FOR_UPDATE)<boolean>();

export const modifyPriceRequest = createStandardAction(OrderActionTypes.MODIFY_PRICE_REQUEST).map((payload: { navigate: NavigateFunction }) => ({
  payload
}));

export const modifyPriceRequestFromCurrencyModal = createStandardAction(OrderActionTypes.MODIFY_PRICE_REQUEST_MODAL).map(
  (payload: { selectedCurrency: string; navigate: NavigateFunction }) => ({
    payload
  })
);

export const splitPriceRequest = createStandardAction(OrderActionTypes.SPLIT_PRICE_REQUEST).map((payload: { navigate: NavigateFunction }) => ({
  payload
}));
